import {
  allPass,
  any,
  chain,
  complement,
  equals,
  filter,
  flip,
  includes,
  map,
  pipe,
} from "ramda";
import { from, of } from "most";
import { combineEpics, select, withState } from "redux-most";
import { mergeArray, recoverWith } from "common/mostCurried";
import * as steps from "common/steps";
import * as introActions from "intro/introActions";
import * as basicDetailsActions from "basic-details/basicDetailsActions";
import * as applicationActions from "application/applicationActions";
import * as submissionActions from "submission/submissionActions";
import {
  doesStepHaveErrors,
  getCountryCode,
  getNextIncompleteStep,
  getProjectId,
  getStepFields,
  isCountriesEmpty,
  isCountryEmpty,
  isLibrariesEmpty,
  isStepCompleted,
} from "common/selectors";
import {
  createSyncLocalStorageEpic,
  createLoadLocalStorageEpic,
  createAcknowledgeSubmissionEpic,
} from "common/stepEpicCommon";
import { fetchLibraries } from "common/authAjax";
import {
  fetchCountries,
  fetchSubmissionsByEmail,
  fetchSubdivisions,
} from "common/stipAjax";

const isStepInitAction = action =>
  action.type === submissionActions.LOCAL_STORAGE_LOADED ||
  action.type === submissionActions.SET_ACTIVE_STEP;

const loadCountriesFilter = ([state, action]) =>
  isStepInitAction(action) && allPass([isCountriesEmpty])(state);

export const loadCountriesEpic = pipe(
  flip(withState),
  filter(loadCountriesFilter),
  chain(
    pipe(
      fetchCountries,
      map(basicDetailsActions.countriesLoaded),
      recoverWith(pipe(applicationActions.appError, of)),
    ),
  ),
);

const loadLibrariesFilter = ([state, action]) =>
  isStepInitAction(action) && allPass([isLibrariesEmpty])(state);

export const loadLibrariesEpic = pipe(
  flip(withState),
  filter(loadLibrariesFilter),
  chain(
    pipe(
      fetchLibraries,
      map(basicDetailsActions.librariesLoaded),
      recoverWith(pipe(applicationActions.appError, of)),
    ),
  ),
);

const loadSubdivisionsFilter = ([state, action]) =>
  any(equals(action.type), [
    submissionActions.SET_ACTIVE_STEP,
    basicDetailsActions.LOCAL_STORAGE_LOADED,
    basicDetailsActions.COUNTRY_CHANGED,
    introActions.COUNTRY_CHANGED,
  ]) && allPass([complement(isCountryEmpty)])(state);

export const loadSubdivisionsEpic = pipe(
  flip(withState),
  filter(loadSubdivisionsFilter),
  chain(
    pipe(
      ([state, action]) => fetchSubdivisions(getCountryCode(state)),
      map(basicDetailsActions.subdivisionsLoaded),
      recoverWith(pipe(applicationActions.appError, of)),
    ),
  ),
);

// TODO: temporary solution for annual contest
const IAP_PROJECT_IDS = [
  "05e2202d-bad5-4636-9db2-9dd902fa18ae", // [PROD] Annual IAP Contest
  "d3ac24d0-043c-4d21-b36d-884ea8301cb1", // [TEST] albums
];

export const completeStepEpic = (action$, state$) =>
  pipe(
    select(basicDetailsActions.COMPLETE_STEP),
    withState(state$),
    chain(([state]) => {
      if (doesStepHaveErrors(steps.BASIC_DETAILS, state)) {
        return of(submissionActions.showValidationErrorsDialog(true));
      }

      const nextStep = getNextIncompleteStep(state) || steps.TERMS;
      const projectId = getProjectId(state);

      if (!includes(projectId, IAP_PROJECT_IDS)) {
        return of(submissionActions.setActiveStep(nextStep));
      }

      const email = getStepFields(steps.BASIC_DETAILS)(state).email;

      return mergeArray([
        of(submissionActions.setExistingSubmissionsChecking(true)),
        pipe(
          fetchSubmissionsByEmail,
          chain(submissions =>
            from([
              submissionActions.setExistingSubmissionsChecking(false),
              Array.isArray(submissions) && submissions.length > 0
                ? submissionActions.showExistingSubmissionsDialog(true, email)
                : submissionActions.setActiveStep(nextStep),
            ]),
          ),
          recoverWith(() =>
            from([
              submissionActions.setExistingSubmissionsChecking(false),
              submissionActions.setActiveStep(nextStep),
            ]),
          ),
        )(email),
      ]);
    }),
  )(action$);

const syncLocalStorageEpic = createSyncLocalStorageEpic(
  [
    submissionActions.CANCEL_SUBMISSION,
    submissionActions.SUBMIT,
    basicDetailsActions.COMPLETE_STEP,
    basicDetailsActions.COUNTRY_CHANGED,
    basicDetailsActions.FIELD_CHANGED,
    basicDetailsActions.LOCAL_LIBRARY_CHANGED,
    basicDetailsActions.REGION_CHANGED,
    basicDetailsActions.SET_SHOW_LOCAL_LIBRARY_OTHER,
    introActions.SUBMITTER_IS_CONTRIBUTOR_CHANGED,
  ],
  {
    completed: isStepCompleted(steps.BASIC_DETAILS),
    fields: getStepFields(steps.BASIC_DETAILS),
  },
  basicDetailsActions.localStorageSynced,
  steps.BASIC_DETAILS,
);

export default combineEpics([
  createAcknowledgeSubmissionEpic(steps.BASIC_DETAILS),
  completeStepEpic,
  createLoadLocalStorageEpic(
    steps.BASIC_DETAILS,
    basicDetailsActions.localStorageLoaded,
  ),
  loadCountriesEpic,
  loadLibrariesEpic,
  loadSubdivisionsEpic,
  syncLocalStorageEpic,
]);
